import { useRecoilState, useRecoilValue } from 'recoil';
import {
  dateRangeAtom,
  type Period,
  periodEndDateAtom,
  periodStartDateAtom,
  periodTypeAtom,
  selectedDeviceStateAtom,
} from '../models/atoms/device_atoms';
import { useTranslation } from '../contexts/TranslationContext';
import dayjs, { type Dayjs } from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import updateLocale from 'dayjs/plugin/updateLocale';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/de';
import { useEffect } from 'react';

dayjs.extend(minMax);
dayjs.extend(updateLocale);

//US date locale starts week on Sunday, we want all to be Monday.
//en required for unit tests, en-us for the actual app.
['en', 'en-us'].forEach(locale => {
  dayjs.updateLocale(locale, {
    weekStart: 1,
  });
});

function clampDate(date: Dayjs, start: Dayjs, end: Dayjs) {
  return dayjs.min(dayjs.max(date, start), end);
}

type ChangeablePeriod = Exclude<Period, 'all' | 'custom'>;

export function canIncreasePeriodHelper(
  periodType: Period,
  periodStartDate: Dayjs,
  dateRangeEnd: Dayjs,
): periodType is ChangeablePeriod {
  if (periodType === 'all' || periodType === 'custom') {
    return false;
  }
  return periodStartDate.add(1, periodType) <= dateRangeEnd;
}

export function canDecreasePeriodHelper(
  periodType: Period,
  periodStartDate: Dayjs,
  dateRangeStart: Dayjs,
): periodType is ChangeablePeriod {
  if (periodType === 'all' || periodType === 'custom') {
    return false;
  }
  const newPeriodStartDate = periodStartDate.subtract(1, periodType);
  const newPeriodEndDate = newPeriodStartDate.endOf(periodType);
  return newPeriodEndDate >= dateRangeStart;
}

type DateRange = {
  start: Dayjs;
  end: Dayjs;
};

export function changePeriodRange(
  periodType: Period,
  periodStartDate: Dayjs,
  periodEndDate: Dayjs,
  dateRangeStart: Dayjs,
  dateRangeEnd: Dayjs,
): DateRange {
  if (periodType === 'custom') {
    return {
      start: periodStartDate,
      end: periodEndDate,
    };
  }
  let newPeriodStartDate = clampDate(
    periodStartDate,
    dateRangeStart,
    dateRangeEnd,
  );
  let newPeriodEndDate = clampDate(periodEndDate, dateRangeStart, dateRangeEnd);
  if (periodType === 'all') {
    newPeriodStartDate = dateRangeStart;
    newPeriodEndDate = dateRangeEnd;
  } else {
    newPeriodStartDate = clampDate(
      newPeriodStartDate.startOf(periodType),
      dateRangeStart,
      dateRangeEnd,
    );
    newPeriodEndDate = clampDate(
      newPeriodStartDate.endOf(periodType),
      dateRangeStart,
      dateRangeEnd,
    );
  }
  return {
    start: newPeriodStartDate,
    end: newPeriodEndDate,
  };
}

export function increasePeriodRange(
  periodType: Period,
  periodStartDate: Dayjs,
  periodEndDate: Dayjs,
  dateRangeStart: Dayjs,
  dateRangeEnd: Dayjs,
): DateRange {
  if (!canIncreasePeriodHelper(periodType, periodStartDate, dateRangeEnd)) {
    return {
      start: periodStartDate,
      end: periodEndDate,
    };
  }
  const newPeriodStartDate = clampDate(
    periodStartDate.add(1, periodType),
    dateRangeStart,
    dateRangeEnd,
  );

  return {
    start: newPeriodStartDate,
    end: clampDate(
      newPeriodStartDate.endOf(periodType),
      dateRangeStart,
      dateRangeEnd,
    ),
  };
}

export function decreasePeriodRange(
  periodType: Period,
  periodStartDate: Dayjs,
  periodEndDate: Dayjs,
  dateRangeStart: Dayjs,
  dateRangeEnd: Dayjs,
): DateRange {
  if (!canDecreasePeriodHelper(periodType, periodStartDate, dateRangeStart)) {
    return {
      start: periodStartDate,
      end: periodEndDate,
    };
  }
  const newPeriodStartDate = clampDate(
    periodStartDate.subtract(1, periodType),
    dateRangeStart,
    dateRangeEnd,
  );
  return {
    start: newPeriodStartDate,
    end: newPeriodStartDate.endOf(periodType),
  };
}

export function earliestPeriodRange(
  periodType: Exclude<Period, 'all' | 'custom'>,
  dateRangeStart: Dayjs,
  dateRangeEnd: Dayjs,
): DateRange {
  let newPeriodStartDate = dateRangeStart;
  let newPeriodEndDate = dateRangeStart;

  newPeriodStartDate = clampDate(
    newPeriodStartDate.startOf(periodType),
    dateRangeStart,
    dateRangeEnd,
  );
  newPeriodEndDate = clampDate(
    newPeriodStartDate.endOf(periodType),
    dateRangeStart,
    dateRangeEnd,
  );

  return {
    start: newPeriodStartDate,
    end: newPeriodEndDate,
  };
}

export function latestPeriodRange(
  periodType: Exclude<Period, 'all' | 'custom'>,
  dateRangeStart: Dayjs,
  dateRangeEnd: Dayjs,
): DateRange {
  let newPeriodStartDate = dateRangeEnd;
  let newPeriodEndDate = dateRangeEnd;

  newPeriodStartDate = clampDate(
    newPeriodStartDate.startOf(periodType),
    dateRangeStart,
    dateRangeEnd,
  );
  newPeriodEndDate = clampDate(
    newPeriodStartDate.endOf(periodType),
    dateRangeStart,
    dateRangeEnd,
  );

  return {
    start: newPeriodStartDate,
    end: newPeriodEndDate,
  };
}

export function useDeviceDataPeriod() {
  const { t } = useTranslation();
  const selectedDevice = useRecoilValue(selectedDeviceStateAtom);
  const dateRange = useRecoilValue(dateRangeAtom);
  const { start: dateRangeStart, end: dateRangeEnd } = dateRange;
  const [periodType, setPeriodType] = useRecoilState(periodTypeAtom);
  const [periodStartDate, setPeriodStartDate] =
    useRecoilState(periodStartDateAtom);
  const [periodEndDate, setPeriodEndDate] = useRecoilState(periodEndDateAtom);

  const periods: [Period, string][] = [
    ['day', t('Day')],
    ['week', t('Week')],
    ['month', t('Month')],
    ['year', t('Year')],
  ];

  useEffect(() => {
    const start = clampDate(periodStartDate, dateRangeStart, dateRangeEnd);
    const end = clampDate(periodEndDate, dateRangeStart, dateRangeEnd);
    if (!start.isSame(periodStartDate)) {
      setPeriodStartDate(start);
    }
    if (!end.isSame(periodEndDate)) {
      setPeriodEndDate(end);
    }
  }, [
    dateRangeStart,
    dateRangeEnd,
    periodStartDate,
    periodEndDate,
    setPeriodStartDate,
    setPeriodEndDate,
  ]);

  const updatePeriod = (start: Dayjs, end: Dayjs) => {
    if (start.isSame(periodStartDate) && end.isSame(periodEndDate)) {
      return;
    }
    if (!start.isSame(periodStartDate)) {
      setPeriodStartDate(start);
    }
    if (!end.isSame(periodEndDate)) {
      setPeriodEndDate(end);
    }
    if (!selectedDevice) {
      return;
    }
  };

  const canIncreasePeriod = (currentPeriodType: Period) => {
    return canIncreasePeriodHelper(
      currentPeriodType,
      periodStartDate,
      dateRangeEnd,
    );
  };

  const canDecreasePeriod = (currentPeriodType: Period) => {
    return canDecreasePeriodHelper(
      currentPeriodType,
      periodStartDate,
      dateRangeStart,
    );
  };

  const changePeriodType = (periodType: Period) => {
    if (periodType === 'custom') {
      setPeriodType('custom');
      return;
    }

    if (periodType === 'all') {
      setPeriodType('all');
      updatePeriod(dateRangeStart, dateRangeEnd);
      return;
    }

    const { start, end } = changePeriodRange(
      periodType,
      periodStartDate,
      periodEndDate,
      dateRangeStart,
      dateRangeEnd,
    );
    setPeriodType(periodType);
    updatePeriod(start, end);
  };

  const increasePeriod = (periodType: Period) => {
    const { start, end } = increasePeriodRange(
      periodType,
      periodStartDate,
      periodEndDate,
      dateRangeStart,
      dateRangeEnd,
    );
    updatePeriod(start, end);
  };

  const decreasePeriod = (periodType: Period) => {
    const { start, end } = decreasePeriodRange(
      periodType,
      periodStartDate,
      periodEndDate,
      dateRangeStart,
      dateRangeEnd,
    );
    updatePeriod(start, end);
  };

  const earliestPeriod = () => {
    if (periodType === 'all' || periodType === 'custom') {
      return;
    }

    const { start, end } = earliestPeriodRange(
      periodType,
      dateRangeStart,
      dateRangeEnd,
    );
    updatePeriod(start, end);
  };

  const latestPeriod = () => {
    if (periodType === 'all' || periodType === 'custom') {
      return;
    }

    const { start, end } = latestPeriodRange(
      periodType,
      dateRangeStart,
      dateRangeEnd,
    );
    updatePeriod(start, end);
  };

  const isEarliestPeriod = (() => {
    if (periodType === 'all' || periodType === 'custom') {
      return false;
    }

    const earliestRange = earliestPeriodRange(
      periodType,
      dateRangeStart,
      dateRangeEnd,
    );
    return (
      earliestRange.start.isSame(periodStartDate) &&
      earliestRange.end.isSame(periodEndDate)
    );
  })();

  const isLatestPeriod = (() => {
    if (periodType === 'all' || periodType === 'custom') {
      return false;
    }

    const latestRange = latestPeriodRange(
      periodType,
      dateRangeStart,
      dateRangeEnd,
    );
    return (
      latestRange.start.isSame(periodStartDate) &&
      latestRange.end.isSame(periodEndDate)
    );
  })();

  return {
    periods,
    periodType,
    periodStartDate,
    periodEndDate,
    isEarliestPeriod,
    isLatestPeriod,
    canIncreasePeriod,
    canDecreasePeriod,
    changePeriodType,
    increasePeriod,
    decreasePeriod,
    earliestPeriod,
    latestPeriod,
    setPeriodStartDate,
    setPeriodEndDate,
  };
}
